import {
  Box3,
  BoxGeometry,
  EdgesGeometry,
  Group,
  LineBasicMaterial,
  LineSegments,
  Mesh,
  type Object3D,
  Vector3
} from "three";
import type { DxfDevicePlacement } from "../cad/cad-device-layout";
import type { DxfMirrorAxes } from "../cad/dxf-insert-transform";
import type { DeviceModelDefinition } from "../models/device-model-catalog";
import { type DeviceModelLoader, deviceModelLibrary } from "../models/device-model-library";
import { disposeObject3DResources } from "../models/shared-model-resources";
import { applyDxfMirror } from "./dxf-mirror";

export class CadDevice extends Group {
  private readonly mirrorAxes: DxfMirrorAxes;
  private modelId = "";
  private modelRoot?: Group;
  private selectionOutline?: LineSegments;

  constructor(placement: DxfDevicePlacement) {
    super();
    this.name = `cad-device-${placement.blockPrefix}`;
    this.userData.cadDeviceId = placement.id;
    this.userData.deviceBlockPrefix = placement.blockPrefix;
    this.position.set(placement.positionX, 0, placement.positionZ);
    this.rotation.y = placement.rotationY;
    this.mirrorAxes = placement;
  }

  static async create(
    placement: DxfDevicePlacement,
    model: DeviceModelDefinition,
    loader: DeviceModelLoader = deviceModelLibrary
  ) {
    const device = new CadDevice(placement);
    device.setModel(model, await loader.load(model));
    return device;
  }

  getModelId() {
    return this.modelId;
  }

  getHitTargets() {
    const targets: Object3D[] = [];
    this.modelRoot?.traverse((child) => {
      if (child instanceof Mesh) {
        targets.push(child);
      }
    });
    return targets;
  }

  setModel(model: DeviceModelDefinition, content: Object3D) {
    const previousRoot = this.modelRoot;
    const previousOutline = this.selectionOutline;
    const modelRoot = createBottomCenteredModelRoot(content);
    const selectionOutline = createSelectionOutline(modelRoot);

    previousRoot?.removeFromParent();
    previousOutline?.removeFromParent();
    this.modelId = model.id;
    this.modelRoot = modelRoot;
    this.selectionOutline = selectionOutline;
    this.userData.deviceModelId = model.id;
    this.userData.deviceModelName = model.displayName;
    this.add(modelRoot, selectionOutline);
    applyDxfMirror(this, this.mirrorAxes);
    disposeObject(previousRoot);
    disposeObject(previousOutline);
  }

  setSelected(selected: boolean) {
    if (this.selectionOutline) {
      this.selectionOutline.visible = selected;
    }
  }
}

function createBottomCenteredModelRoot(content: Object3D) {
  content.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(content);
  const center = bounds.getCenter(new Vector3());
  const modelRoot = new Group();

  modelRoot.name = "cad-device-model-root";
  content.position.x -= center.x;
  content.position.y -= bounds.min.y;
  content.position.z -= center.z;
  content.updateWorldMatrix(true, true);
  modelRoot.add(content);
  return modelRoot;
}

function createSelectionOutline(modelRoot: Object3D) {
  modelRoot.updateWorldMatrix(true, true);
  const bounds = new Box3().setFromObject(modelRoot);
  const size = bounds.getSize(new Vector3());
  const center = bounds.getCenter(new Vector3());
  const boxGeometry = new BoxGeometry(size.x, size.y, size.z);
  const geometry = new EdgesGeometry(boxGeometry);
  boxGeometry.dispose();
  const outline = new LineSegments(
    geometry,
    new LineBasicMaterial({ color: "#ffffff", depthTest: false, depthWrite: false })
  );
  outline.name = "device-selection-highlight";
  outline.position.copy(center);
  outline.renderOrder = 80;
  outline.visible = false;
  return outline;
}

function disposeObject(object?: Object3D) {
  if (object) {
    disposeObject3DResources(object);
  }
}
